import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { SearchInput } from "../../../../Forms/Inputs/SearchInput/React";
import { ErrorMessage } from "../../components/shared/errorMessage";
import LoadingStatus from "../../components/shared/loadingStatus";
import { Spinner } from "../../components/shared/spinner";
import { useProvetContext, useProvetData, useTranslationContext } from "../../context";
import { useFocusAfterReload } from "../../hooks/useFocusAfterReload";
import { Reason, ReasonGroup, ReasonGroupTuple } from "../../types";
import { LOADING_CONFIG } from "../../utilities/provetConstants";
import ReasonItem, { getTreatmentAnimalsLabel } from "./reasonItem";

interface ProvetTreatmentFinderProps {
  onSelectReason: (reason: Reason) => void;
  /**
   * A treatment from a link is waiting for the treatments to load. Only the loading state is
   * shown, so the list (and its search field) doesn't appear and then collapse.
   */
  isPreselectionPending?: boolean;
}

/**
 * Treatment (reason) finder component for selecting the purpose of a veterinary visit.
 *
 * Features:
 * - Search treatments by name or category
 * - Filter by treatment categories (reason groups)
 * - Automatically filters treatments based on selected department
 * - Grouped display by treatment categories
 * - Auto-clears search after selection
 * - A treatment that is only for certain animals says so, eg "For: Dog, Cat" (D9)
 * - Error with "Try again" when the treatments fail to load, and a message when the clinic
 *   has no treatments or a search or filter matches nothing
 *
 * The reason groups (categories) and reasons (individual treatments) are fetched once by
 * ProvetDataProvider; this component filters them based on the currently selected department.
 *
 * @param onSelectReason - Callback invoked when user selects a treatment/reason.
 *                         Receives the selected Reason object.
 *
 * @example
 * ```tsx
 * <ProvetTreatmentFinder
 *   onSelectReason={(reason) => {
 *     console.log('Selected treatment:', reason.name);
 *     handleReasonSelection(reason);
 *   }}
 * />
 * ```
 */
export const ProvetTreatmentFinder = ({
  onSelectReason,
  isPreselectionPending = false
}: ProvetTreatmentFinderProps) => {
  const translationContext = useTranslationContext();
  const provetContext = useProvetContext();
  const { species } = useProvetData();
  const treatmentForAnimals = translationContext?.translations?.treatmentForAnimals;

  // State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { targetRef: resultsRef, retry: retryReasons } = useFocusAfterReload(
    provetContext.reasonsStatus,
    provetContext.reloadReasons
  );

  const loadError = useMemo(
    () => new Error(translationContext?.translations?.errorLoadingTreatments ?? ""),
    [translationContext?.translations?.errorLoadingTreatments]
  );

  // Helper function to get reasons for a reason group
  const getReasons = useCallback(
    (reasonGroup: ReasonGroup): Reason[] => {
      if (!provetContext.reasons || provetContext.reasons.length === 0) {
        return [];
      }

      return provetContext.reasons.filter(reason => {
        const groupUrl = reason.group;
        return groupUrl && groupUrl.endsWith(`/${reasonGroup.id}/`);
      });
    },
    [provetContext.reasons]
  );

  const selectedDepartmentId =
    provetContext.bookingFlowState.formData?.selectedDepartment?.id;

  // The selected clinic's treatments. Derived (not set in an effect), so the list and its
  // "no treatments" message never show a previous clinic's state for a render.
  const reasonItems = useMemo((): ReasonGroupTuple[] => {
    if (
      selectedDepartmentId &&
      provetContext.reasonGroups.length > 0 &&
      provetContext.reasons.length > 0
    ) {
      // Filter reason groups by department (matching Blazor logic)
      // The Blazor code checks: r.DepartmentUrl.EndsWith($"/{SelectedDepartment.DepartmentId}/")
      const departmentReasonGroups = provetContext.reasonGroups.filter(reasonGroup => {
        const departmentUrl = reasonGroup.department;
        return departmentUrl && departmentUrl.endsWith(`/${selectedDepartmentId}/`);
      });

      // Create tuples of ReasonGroup and Reason (matching Blazor SelectMany logic)
      const newReasonItems: ReasonGroupTuple[] = [];

      departmentReasonGroups.forEach(reasonGroup => {
        const reasonsForGroup = getReasons(reasonGroup);
        reasonsForGroup.forEach(reason => {
          newReasonItems.push({
            reasonGroup: reasonGroup,
            reason: reason
          });
        });
      });

      return newReasonItems;
    }

    return [];
  }, [
    selectedDepartmentId,
    provetContext.reasonGroups,
    provetContext.reasons,
    getReasons
  ]);

  // Extract unique categories from reasons
  const filterCategories = useMemo(
    () =>
      Array.from(
        new Set(
          reasonItems
            .map(item => item.reasonGroup.name)
            .filter((name): name is string => !!name)
        )
      ),
    [reasonItems]
  );

  // Derive filtered items instead of storing in state
  const filteredReasonItems = useMemo(() => {
    let filtered = reasonItems;

    if (selectedCategories.length > 0) {
      filtered = filtered.filter(
        item =>
          item.reasonGroup.name && selectedCategories.includes(item.reasonGroup.name)
      );
    }

    if (!searchTerm.trim()) {
      return filtered;
    }

    const searchLower = searchTerm.toLowerCase();
    return filtered.filter(
      reasonItem =>
        (reasonItem.reasonGroup.name || "").toLowerCase().includes(searchLower) ||
        (reasonItem.reason.name || "").toLowerCase().includes(searchLower)
    );
  }, [reasonItems, searchTerm, selectedCategories]);

  // Always rendered in a role="status" region, so changes to it are announced
  let listMessage = "";
  if (provetContext.reasonsStatus === "loaded" && selectedDepartmentId) {
    if (reasonItems.length === 0) {
      listMessage = translationContext?.translations?.noTreatmentsAvailable ?? "";
    } else if (
      (!!searchTerm.trim() || selectedCategories.length > 0) &&
      filteredReasonItems.length === 0
    ) {
      listMessage = translationContext?.translations?.noTreatmentsFound ?? "";
    }
  }

  // Simple event handlers - no memoization needed for basic setters
  const handleSearchChange = (query?: string) => {
    setSearchTerm(query || "");
  };

  const handleFilterChange = (selected: string[]) => {
    setSelectedCategories(selected);
  };

  // Reason selection handler - needs memoization as it's passed to child components
  const reasonSelected = useCallback(
    async (reason: Reason) => {
      if (onSelectReason) {
        onSelectReason(reason);
      }

      // Reset search after selection
      setSearchTerm("");
    },
    [onSelectReason]
  );

  const loadingStatus = (
    <LoadingStatus
      loading={true}
      message={translationContext?.translations?.loadingTreatments}
      className="justify-center py-xl"
      style={{ minHeight: `${LOADING_CONFIG.TREATMENT_MIN_HEIGHT}px` }}
    >
      <Spinner visible={true} />
    </LoadingStatus>
  );

  if (isPreselectionPending) {
    return loadingStatus;
  }

  // While a treatment is being selected, the list is locked (see useBookingModalActions)
  const isSelecting = provetContext.bookingFlowState.loadingAvailableAppointmentTimes;

  return (
    <div className="flex flex-col gap-md @container/treatment-finder">
      <SearchInput
        onInput={handleSearchChange}
        onSubmit={handleSearchChange}
        name="treatment-search"
        placeholder={translationContext?.translations?.searchTreatment}
        additionalClassName="treatment-search-input"
        filterCategories={filterCategories}
        onFilterChange={handleFilterChange}
      />
      {/* A stable focus target for after "Try again" */}
      <div
        ref={resultsRef}
        tabIndex={-1}
        className="flex flex-col gap-md outline-none"
        data-online-booking-treatment-results
      >
        {provetContext.reasonsStatus === "error" && (
          <ErrorMessage error={loadError} onRetry={retryReasons} />
        )}

        {provetContext.reasonsStatus === "loading" && loadingStatus}

        {provetContext.reasonsStatus === "loaded" && (
          <ul
            className={classNames(
              "flex flex-col w-[100cqw] max-h-[27.375rem] overflow-y-auto",
              { "pointer-events-none opacity-60": isSelecting }
            )}
            aria-busy={isSelecting}
            data-online-booking-treatment-list
          >
            {filteredReasonItems &&
              filteredReasonItems.map((reasonItem, index) => (
                <li key={reasonItem.reason.id ?? `index-${index}`}>
                  <ReasonItem
                    reason={reasonItem}
                    animalsLabel={getTreatmentAnimalsLabel(
                      reasonItem.reason,
                      species,
                      treatmentForAnimals
                    )}
                    onSelectReason={() => reasonSelected(reasonItem.reason)}
                  />
                </li>
              ))}
          </ul>
        )}

        <p
          className={classNames("text-t-sm text-t-neutral-700", {
            "py-md": !!listMessage
          })}
          role="status"
          data-online-booking-no-results
        >
          {listMessage}
        </p>
      </div>{" "}
    </div>
  );
};

export default ProvetTreatmentFinder;
